<template>
    <div>
        <simple-modal :show="show" max-width="sm:max-w-4xl" title="Migrate Data To Other Team" @close="closeModal()">
            <div class="flex flex-col gap-6">
                <!-- TEAM -->
                <div class="flex flex-col gap-4">
                    <label class="flex items-center font-medium gap-1">
                        Copy data from <span class="font-bold">{{ originalTeam.reference }} - </span>
                        <span class="flex items-center text-indigo-600 gap-1">
                            {{ originalTeam.company_name }}
                            <span class="text-gray-400" v-if="originalTeam.trading_name">{{ " trading as " + originalTeam.trading_name }}</span>
                        </span>
                    </label>

                    <div class="flex items-center gap-4">
                        <label class="font-medium whitespace-nowrap">To Team: </label>

                        <remote-select
                            v-model="selectedTeam"
                            @focus="searchTeams"
                            @input="setTargetTeamId"
                            :remote-method="searchTeams"
                            :data="teams"
                            class="w-full"
                            item-label="trading_name"
                            placeholder="Search Teams">
                            <template slot-scope="props">
                                <span class="flex items-center text-indigo-600 gap-1">
                                    {{ "[" + props.item.id + "] " + (props.item.company_name ? props.item.company_name : 'No Company Name') }}

                                    <span class="text-gray-400" v-if="props.item.trading_name">{{ " trading as " + props.item.trading_name }}</span>

                                    {{
                                        " [" + (props.item.subscription ? props.item.subscription.pricing_plan : "no valid subscription") + " - "
                                        + (props.item.subscription ? props.item.subscription.status : "no valid subscription") + "]"
                                    }}
                                </span>
                            </template>
                        </remote-select>
                    </div>
                </div>

                <template v-if="form.team.target_team_id">
                    <!-- ASSETS -->
                    <div class="flex flex-col gap-4">
                        <checkbox v-model="form.assets.should_copy" label="Copy Assets" label-styling="text-sm font-medium text-indigo-600"></checkbox>

                        <div class="flex flex-col gap-2 ml-4" v-if="form.assets.should_copy">
                            <portfolio-select-section :selection="assetPortfolios" :portfolios="currentPortfolios" :form="form"
                                                      @update-portfolio-selection="setPortfolioSection"
                                                      @clear-portfolio-errors="clearPortfolioErrors">
                            </portfolio-select-section>
                        </div>
                    </div>

                    <!-- CONTACTS -->
                    <div class="flex flex-col gap-4">
                        <checkbox v-model="form.contacts.should_copy" label="Copy Contacts" label-styling="text-sm font-medium text-indigo-600"></checkbox>
                    </div>

                    <!-- INSPECTIONS -->
                    <div class="flex flex-col gap-4">
                        <checkbox v-model="form.inspections.should_copy" label="Copy Inspections" label-styling="text-sm font-medium text-indigo-600"></checkbox>

                        <div class="flex flex-col gap-2 ml-4" v-if="form.inspections.should_copy">
                            <div class="flex flex-wrap items-center gap-2">
                                <span class="font-medium">Assign to inspector</span>

                                <text-input v-model="form.inspections.inspector_email" placeholder="Inspector email"></text-input>

                                <div class="text-red-500" v-if="form.errors.has('inspections.inspector_email')">
                                    {{ form.errors.get("inspections.inspector_email") }}
                                </div>
                            </div>

                            <div class="flex flex-wrap items-center gap-2">
                                <span class="font-medium">Only copy inspections starting from</span>

                                <el-date-picker
                                    :disabled="form.busy"
                                    v-model="form.inspections.starting_date"
                                    placeholder="Pick A Date"
                                    value-format="yyyy-MM-dd">
                                </el-date-picker>
                            </div>

                            <checkbox class="ml-4" v-if="form.inspections.starting_date" v-model="form.inspections.replicate_copies"
                                      label="Replicate Copies" label-styling="font-medium"
                                      description="When inspection was copied from previous inspections before this date, also copy previous ones">
                            </checkbox>

                            <checkbox v-model="form.inspections.include_archived" label="Include archived" label-styling="font-medium"></checkbox>

                            <checkbox v-model="form.inspections.include_deleted" label="Include deleted" label-styling="font-medium"></checkbox>

                            <portfolio-select-section :selection="inspectionPortfolios" :portfolios="currentPortfolios" :form="form"
                                                      @update-portfolio-selection="setPortfolioSection"
                                                      @clear-portfolio-errors="clearPortfolioErrors">
                            </portfolio-select-section>
                        </div>
                    </div>

                    <!-- TODOS -->
                    <div class="flex flex-col gap-4">
                        <checkbox v-model="form.todos.should_copy" label="Copy To-dos" label-styling="text-sm font-medium text-indigo-600"></checkbox>
                        <div class="flex flex-col gap-2 ml-4" v-if="form.todos.should_copy">
                            <checkbox v-model="form.todos.assign_todos.account_owner" label="Assign to account owner" label-styling="font-medium"></checkbox>

                            <checkbox v-model="form.todos.assign_todos.by_portfolio" label="Assign by users' portfolio access" label-styling="font-medium"></checkbox>

                            <div class="flex flex-wrap items-center gap-2">
                                <label class="font-medium">Assign by user emails</label>

                                <div class="flex gap-2">
                                    <text-input v-model="todoAssignedUserEmail" placeholder="Enter email, click to add to list"></text-input>

                                    <button @click.prevent="addAssignedTodoUser()" @keydown.enter="addAssignedTodoUser()">
                                        <icon-plus-circle class="w-4 h-4 hover:text-indigo-600 transition duration-300"></icon-plus-circle>
                                    </button>
                                </div>
                            </div>

                            <div v-if="form.todos.assign_todos.user_emails" class="flex flex-col gap-2">
                                <div v-for="(email, index) in form.todos.assign_todos.user_emails" class="flex items-center gap-2 ml-6">
                                    {{ email }}

                                    <button @click.prevent="removeAssignedTodoUser(email)">
                                        <icon-trash class="w-4 h-4 hover:text-red-600 transition duration-300"></icon-trash>
                                    </button>
                                    <div class="text-red-500" v-if="form.errors.has('todos.assign_todos.user_emails.' + index)">
                                        {{ form.errors.get("todos.assign_todos.user_emails." + index) }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="flex justify-end" v-if="selectedTeam && anyModelSelectedForCopy">
                    <secondary-button @click.prevent="summarise()">Summarise Migration</secondary-button>
                </div>

                <div v-if="summary" class="border-b border-gray-500"></div>

                <div v-if="summarising" class="flex justify-center items-center font-medium text-lg text-indigo-600">
                    <icon-refresh class="h-6 w-6 animate-spin"/> &nbsp;Loading Summary...
                </div>

                <migration-summary v-if="!summarising && summary" :summary="summary" :form="form"
                                   @show-migration-confirmation="toggleConfirmationDialog()">
                </migration-summary>
            </div>
        </simple-modal>

        <confirmation-dialog :show="showConfirmationModal" v-if="showConfirmationModal"
                             @proceed="migrate()" @cancelled="toggleConfirmationDialog()"
                             title="Confirm Team Data Migration" subtitle="Are you sure you want to proceed with the migration?"
                             proceed-button-text="Confirm Migration" :show-icon="false">
            <template slot="body">
                <div class="mt-4 flex flex-col gap-y-4">
                    <badge class="bg-yellow-100 text-yellow-600" text-size="text-normal">
                        This migration is an irreversible process. Please ensure you have the correct options and target team selected.
                    </badge>

                    <input type="text" class="form-control" v-model="confirmationText" placeholder="Type IRREVERSIBLE to proceed">
                </div>
            </template>
        </confirmation-dialog>
    </div>
</template>

<script>
import PortfolioSelectSection from "./MigrateTeamPortfolioSelect.vue";
import MigrationSummary from "./MigrateTeamSummary.vue";

export default {
    name: "MigrateTeamDataModal",

    components: {
        PortfolioSelectSection,
        MigrationSummary,
    },

    props: {
        show: {
            type: Boolean,
            required: true
        },
        originalTeam: {
            type: Object,
            required: true
        }
    },

    data () {
        return {
            teams: [],
            selectedTeam: null,
            summarising: false,
            summary: null,
            showConfirmationModal: false,
            confirmationText: "",
            todoAssignedUserEmail: "",

            form: new AppForm({
                team: {
                    target_team_id: null,
                },
            }),
        };
    },

    computed: {
        anyModelSelectedForCopy () {
            return this.form.assets.should_copy || this.form.contacts.should_copy || this.form.inspections.should_copy || this.form.todos.should_copy;
        },

        computedFormToBeWatched () {
            // required to get a proper oldValue and newValue in watcher
            return JSON.stringify(this.form);
        },

        assetPortfolios () {
            return this.getPortfolioSection("assets");
        },

        inspectionPortfolios () {
            return this.getPortfolioSection("inspections");
        },

        currentPortfolios () {
            return this.state.currentTeam.portfolios
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(portfolio => {
                return {
                    id: portfolio.id,
                    name: portfolio.name,
                };
            });
        }
    },

    watch: {
        computedFormToBeWatched: {
            deep: true,
            handler (newFormJSON, oldFormJSON) {
                let newForm = JSON.parse(newFormJSON),
                    oldForm = JSON.parse(oldFormJSON);

                // we don't clear the summary on these changes
                delete newForm.busy;
                delete newForm.successful;
                delete oldForm.busy;
                delete oldForm.successful;
                if (!_.isEqual(newForm, oldForm)) {
                    this.summary = null;
                }
            }
        },
    },

    methods: {
        setupForm () {
            this.form = new AppForm({
                team: {
                    target_team_id: null,
                },
                assets: {
                    should_copy: false,
                    copy_from_portfolios: false,
                    portfolio_ids: [],
                    remember_portfolio_names: false,
                    target_portfolio_name: null,
                },
                contacts: {
                    should_copy: false,
                },
                inspections: {
                    should_copy: false,
                    copy_from_portfolios: false,
                    portfolio_ids: [],
                    remember_portfolio_names: false,
                    target_portfolio_name: null,
                    inspector_email: null,
                    starting_date: null,
                    replicate_copies: false,
                    include_archived: false,
                    include_deleted: false,
                },
                todos: {
                    should_copy: false,
                    assign_todos: {
                        account_owner: false,
                        by_portfolio: false,
                        user_emails: [],
                    }
                },
            });
        },

        searchTeams (query) {
            this.loading = true;
            axios.get("/settings/team/search", {params: {filter: query, exclude_team_id: this.originalTeam.id}})
            .then(response => {
                this.teams = response.data;
            })
            .catch(error => {
                this.toastr.error("Error loading teams: " + error.message);
            });
        },

        setTargetTeamId () {
            this.setupForm();
            this.$set(this.form.team, "target_team_id", this.selectedTeam?.id);
        },

        closeModal () {
            this.$emit("close");
        },

        summarise () {
            this.summarising = true;
            App.post("/settings/team/summariseMigration", this.form)
            .then(response => {
                this.summary = response;
                this.summarising = false;
            })
            .catch(error => {
                this.summarising = false;
                toastr.error("Error summarising the migration data. Error: " + error.message);
            });
        },

        toggleConfirmationDialog () {
            this.showConfirmationModal = !this.showConfirmationModal;
        },

        migrate () {
            if (this.confirmationText !== "IRREVERSIBLE") {
                toastr.error("Please type IRREVERSIBLE to proceed");
                return;
            }

            this.toggleConfirmationDialog();
            this.confirmationText = "";
            this.loading = true;
            App.post("/settings/team/migrate", this.form)
            .then(response => {
                toastr.success(response);
                this.selectedTeam = null;
                this.setupForm();
                this.closeModal();
                this.loading = false;
            })
            .catch(error => {
                this.loading = false;
                toastr.error("Error launching the migration process. Error: " + error.message);
            });
        },

        addAssignedTodoUser () {
            if (!this.todoAssignedUserEmail) {
                toastr.error("Please enter a user email");
                return;
            }

            if (this.form.todos.assign_todos.user_emails.includes(this.todoAssignedUserEmail)) {
                toastr.error("User email already added");
            } else {
                this.form.todos.assign_todos.user_emails.push(this.todoAssignedUserEmail);
            }

            this.todoAssignedUserEmail = "";
        },

        removeAssignedTodoUser (email) {
            let foundIndex = this.form.todos.assign_todos.user_emails.indexOf(email);

            if (foundIndex !== -1) {
                this.form.todos.assign_todos.user_emails.splice(foundIndex, 1);
                this.clearTodoUserErrors();
            } else {
                toastr.error("Unable to remove user email");
            }
        },

        clearTodoUserErrors () {
            for (let i = 0; i < this.form.assign_todos.user_emails.length; i++) {
                if (this.form.errors.has("todos.assign_todos.user_emails." + i)) {
                    this.form.errors.forget("todos.assign_todos.user_emails." + i);
                }
            }
        },

        getPortfolioSection (section) {
            if (section in this.form) {
                return {
                    copy_from_portfolios: this.form[section].copy_from_portfolios,
                    portfolio_ids: this.form[section].portfolio_ids,
                    remember_portfolio_names: this.form[section].remember_portfolio_names,
                    target_portfolio_name: this.form[section].target_portfolio_name,
                    disabled: this.isPortfolioSectionDisabled(section),
                    section: section,
                };
            }
            return {};
        },

        setPortfolioSection (portfolioSelection) {
            this.$set(this.form[portfolioSelection.section], "copy_from_portfolios", portfolioSelection.copy_from_portfolios);
            this.$set(this.form[portfolioSelection.section], "portfolio_ids", portfolioSelection.portfolio_ids);
            this.$set(this.form[portfolioSelection.section], "remember_portfolio_names", portfolioSelection.remember_portfolio_names);
            this.$set(this.form[portfolioSelection.section], "target_portfolio_name", portfolioSelection.target_portfolio_name);
            this.$set(this.form[portfolioSelection.section], "disabled", this.isPortfolioSectionDisabled(portfolioSelection.section));
        },

        clearPortfolioErrors (section) {
            if (this.form.errors.has(section + ".portfolio_ids")) {
                this.form.errors.forget(section + ".portfolio_ids");
            }
        },

        isPortfolioSectionDisabled (section) {
            switch (section) {
                case "assets":
                    section = "inspections";
                    break;
                case "inspections":
                    section = "assets";
                    break;
            }
            return this.form[section]?.remember_portfolio_names === true || !!this.form[section]?.target_portfolio_name;
        }
    },
};
</script>